import config from "../config";
import {
  getCustomerPortalSession,
  clearCustomerPortalSession,
  normalizeRequiredPortalUser,
  hasRequiredPortalUser,
  sessionMatchesRequiredPortalUser,
} from "modules/customer-portal/constants/auth";

export const CUSTOMER_PORTAL_LOCKOUT_HOUR_OPTIONS = [12, 24, 48, 72];

export const CUSTOMER_PORTAL_SETTING_DEFAULTS = {
  module_enabled: false,
  allow_client_edit: true,
  allow_client_cancel: true,
  allow_client_reschedule: true,
  waitlist_enabled: true,
  social_login_enabled: false,
  client_change_lockout_enabled: false,
  client_change_lockout_hours: 24,
};

const BOOLEAN_SETTING_KEYS = [
  "module_enabled",
  "allow_client_edit",
  "allow_client_cancel",
  "allow_client_reschedule",
  "waitlist_enabled",
  "social_login_enabled",
  "client_change_lockout_enabled",
];

function coercePortalSettingValue(value, defaultEnabled = true) {
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }

  if (value === true || value === 1 || value === "1" || value === "true") {
    return true;
  }

  return defaultEnabled;
}

function normalizeLockoutHours(value) {
  const hours = Number(value);
  return CUSTOMER_PORTAL_LOCKOUT_HOUR_OPTIONS.includes(hours) ? hours : CUSTOMER_PORTAL_SETTING_DEFAULTS.client_change_lockout_hours;
}

export const getCustomerPortalSettings = (settings) => {
  let parsed = settings;

  if (typeof settings === "string") {
    try {
      parsed = JSON.parse(settings);
    } catch (error) {
      parsed = {};
    }
  }

  const merged = {
    ...CUSTOMER_PORTAL_SETTING_DEFAULTS,
    ...(parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {}),
  };

  const normalized = {};

  BOOLEAN_SETTING_KEYS.forEach((key) => {
    normalized[key] = coercePortalSettingValue(merged[key], CUSTOMER_PORTAL_SETTING_DEFAULTS[key]);
  });

  normalized.client_change_lockout_hours = normalizeLockoutHours(merged.client_change_lockout_hours);

  return normalized;
};

export const isCustomerPortalSettingEnabled = (settings, key) => {
  const normalized = getCustomerPortalSettings(settings);
  if (key !== "module_enabled" && !normalized.module_enabled) {
    return false;
  }
  return normalized[key] === true;
};

export const isCustomerPortalModuleEnabled = (settings) => {
  return isCustomerPortalSettingEnabled(settings, "module_enabled");
};

const normalizePublicUrl = (url) => {
  const trimmed = String(url || "").trim().replace(/\/+$/, "");
  if (!trimmed) {
    return "";
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `http://${trimmed}`;
};

const CUSTOMER_PORTAL_PAGE_PATHS = {
  login: { standalone: "/login", embedded: "/customer-portal/login" },
  dashboard: { standalone: "/dashboard", embedded: "/customer-portal/dashboard" },
  waitlist: { standalone: "/waitlist", embedded: "/customer-portal/waitlist" },
};

const getCustomerPortalOrigin = () => {
  const configured = normalizePublicUrl(config.customerPortalPublicUrl);
  if (configured) {
    return configured;
  }

  if (typeof window === "undefined") {
    return "";
  }

  const { hostname, protocol } = window.location;
  if (hostname === "app.beautisoftware.com") {
    return "https://customer-portal.beautisoftware.com";
  }
  if (hostname === "staging.beautisoftware.com") {
    return "https://staging-customer.beautisoftware.com";
  }
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//${hostname}:3001`;
  }

  return "";
};

const buildCustomerPortalQuery = (query) => {
  if (!query || typeof query !== "object") {
    return "";
  }

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    params.set(key, String(value));
  });

  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

export const isCustomerPortalEnvConfigured = () => {
  if (config.rootApp === "customer" || config.rootApp === "both") {
    return true;
  }
  return Boolean(getCustomerPortalOrigin());
};

export const getCustomerPortalPageHref = (page, options = {}) => {
  const paths = CUSTOMER_PORTAL_PAGE_PATHS[page] || CUSTOMER_PORTAL_PAGE_PATHS.dashboard;
  const query = buildCustomerPortalQuery(options.query);
  const customerUrl = getCustomerPortalOrigin();
  const rootApp = (config.rootApp || "main").toLowerCase();

  if (rootApp === "customer" || rootApp === "both") {
    try {
      const target = new URL(customerUrl);
      if (typeof window !== "undefined" && target.host && target.host !== window.location.host) {
        return `${customerUrl}${paths.standalone}${query}`;
      }
    } catch (error) {
      /* use embedded routes when customer URL is not a full origin */
    }
    return `${paths.embedded}${query}`;
  }

  return customerUrl ? `${customerUrl}${paths.standalone}${query}` : null;
};

export const getCustomerPortalLoginPath = () => getCustomerPortalPageHref("login");

export const getCustomerPortalDashboardPath = () => getCustomerPortalPageHref("dashboard");

export const goToCustomerPortalPage = (page, navigate, options = {}) => {
  const required = normalizeRequiredPortalUser(options.requiredUser);
  const session = getCustomerPortalSession();
  const forceLogin = Boolean(options.forceLogin);
  const query = { ...(options.query || {}) };
  let targetPage = page;

  if (required.userId) {
    query.user_id = required.userId;
  }

  if (forceLogin && !hasRequiredPortalUser(required)) {
    query.reauth = "1";
    targetPage = "login";
    if (session) {
      clearCustomerPortalSession();
    }
  } else if (hasRequiredPortalUser(required) && session && !sessionMatchesRequiredPortalUser(session, required)) {
    clearCustomerPortalSession();
  }

  const href = getCustomerPortalPageHref(targetPage, { ...options, query });
  if (!href) {
    return false;
  }

  if (/^https?:\/\//i.test(href)) {
    window.location.assign(href);
    return true;
  }

  if (typeof navigate === "function") {
    navigate(href, {
      replace: options.replace !== false,
      state: options.state,
    });
    return true;
  }

  window.location.assign(href);
  return true;
};
